// src/admin/components/access-urls/GenerateUrlsModal.jsx
import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import AdminModal from '../common/AdminModal';
import FormInput from '../../../shared/components/FormInput';
import FormSelect from '../../../shared/components/FormSelect';
import useAdminWorkflows from '../../hooks/useAdminWorkflows';

/**
 * Modal for generating a new batch of short URLs.
 *
 * Optionally allows pre-assigning a published User Workflow to every URL in
 * the new batch — useful when generating dedicated demo / feedback codes.
 */
export default function GenerateUrlsModal({ show, onClose, onGenerate }) {
  const [count, setCount] = useState(10);
  const [name, setName] = useState('');
  const [workflowId, setWorkflowId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const { data: workflows, refresh: refreshWorkflows } = useAdminWorkflows();

  // Reset form when modal opens
  useEffect(() => {
    if (show) {
      setCount(10);
      setName('');
      setWorkflowId('');
      setError(null);
      setIsSubmitting(false);
      refreshWorkflows();
    }
  }, [show, refreshWorkflows]);

  const userWorkflowOptions = useMemo(
    () => (workflows || []).filter((w) => w.user_workflow && w.state === 'published'),
    [workflows]
  );
  const draftUserWorkflowCount = useMemo(
    () => (workflows || []).filter((w) => w.user_workflow && w.state !== 'published').length,
    [workflows]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      await onGenerate(count, name, workflowId || null);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to generate URLs');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isValid = count >= 1 && count <= 1024;

  const footer = (
    <div className="d-flex justify-content-between w-100">
      <button
        type="button"
        className="btn btn-link text-muted"
        onClick={onClose}
        disabled={isSubmitting}
      >
        <i className="bi bi-arrow-left me-1" /> Cancel
      </button>
      <button
        type="submit"
        form="generate-urls-form"
        className="btn btn-primary"
        disabled={!isValid || isSubmitting}
      >
        {isSubmitting ? (
          <>
            <span className="spinner-border spinner-border-sm me-2" role="status" />
            Generating...
          </>
        ) : (
          <>
            <i className="bi bi-plus-lg me-1" />
            Generate URLs
          </>
        )}
      </button>
    </div>
  );

  return (
    <AdminModal
      show={show}
      onClose={onClose}
      title="Generate New Access URLs"
      footer={footer}
      size=""
    >
      <form id="generate-urls-form" onSubmit={handleSubmit}>
        {/* Help text */}
        <div className="alert alert-light border mb-4">
          <i className="bi bi-info-circle me-2" />
          <span>
            Generate a batch of unique short URLs that can be printed as QR codes and attached to assets.
            Once generated, URLs can be claimed (associated with specific assets) when labels are applied in the field.
          </span>
        </div>

        {error && (
          <div className="alert alert-danger mb-3">
            <i className="bi bi-exclamation-triangle me-2" />
            {error}
          </div>
        )}

        {/* Number of URLs */}
        <FormInput
          id="url-count"
          type="number"
          label="Number of URLs to generate"
          value={count}
          onChange={(e) => setCount(parseInt(e.target.value, 10) || 0)}
          helperText="Enter a number between 1 and 1024. Consider generating extra codes for replacements."
          min={1}
          max={1024}
          required
        />

        {/* Batch name (optional) */}
        <FormInput
          id="batch-name"
          label={<>Batch name <span className="text-muted">(optional)</span></>}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g., Building A - Floor 2"
          helperText="A descriptive name to help identify this batch later (e.g., deployment location or purpose)."
        />

        {/* Optional User Workflow pre-assignment */}
        {userWorkflowOptions.length > 0 && (
          <div className="mb-3">
            <FormSelect
              id="batch-workflow"
              label={<>Pre-assign User Workflow <span className="text-muted">(optional)</span></>}
              value={workflowId}
              onChange={(e) => setWorkflowId(e.target.value)}
              placeholder="— None (regular asset access links) —"
              options={userWorkflowOptions.map((w) => ({ value: w.id, label: w.name }))}
              helperText="When set, every URL in this batch will direct-launch the chosen User Workflow as soon as it is claimed to an asset. You can still change individual URLs later."
              className="mb-0"
            />
            {draftUserWorkflowCount > 0 && (
              <div className="alert alert-warning mt-2 mb-0 py-2 small">
                <i className="bi bi-exclamation-triangle me-2" />
                {draftUserWorkflowCount === 1
                  ? '1 additional User Workflow is still in draft and not selectable here. Publish it to use it as a direct-launch workflow.'
                  : `${draftUserWorkflowCount} additional User Workflows are still in draft and not selectable here. Publish them to use them as direct-launch workflows.`}
              </div>
            )}
          </div>
        )}

        {userWorkflowOptions.length === 0 && draftUserWorkflowCount > 0 && (
          <div className="alert alert-warning mb-3 small">
            <i className="bi bi-exclamation-triangle me-2" />
            {draftUserWorkflowCount === 1
              ? 'A User Workflow exists but is still in draft. Publish it to pre-assign it to a batch as a direct-launch workflow.'
              : `${draftUserWorkflowCount} User Workflows exist but are still in draft. Publish at least one of them to pre-assign it to a batch as a direct-launch workflow.`}
          </div>
        )}
      </form>
    </AdminModal>
  );
}

GenerateUrlsModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onGenerate: PropTypes.func.isRequired,
};
